/**
 * SVG ロゴを **オフライン 60fps** で動画として書き出すユーティリティ。
 *
 * 仕組み:
 *   1. アニメを停止 (playing=false) し、ループ全長を 1/fps 刻みで分割
 *   2. 各フレームで `elapsed` を進めて SVG を強制更新
 *   3. SVG → Image → offscreen canvas にラスタライズ
 *   4. canvas を VideoFrame にラップして WebCodecs `VideoEncoder` に投入
 *   5. エンコード済みチャンクを mp4-muxer / webm-muxer で MP4/WebM に詰める
 *   6. ArrayBuffer → Blob → ダウンロード
 *
 * 実時間に縛られないため、出力 fps は完全に決定論的 (60fps を取りこぼさない)。
 * 書き出し時間は loopDuration の数倍だが、フレームレートは保証される。
 *
 * 依存:
 *   - WebCodecs API (`VideoEncoder` / `VideoFrame`) … Chrome/Edge 94+, Safari 16.4+
 *   - mp4-muxer / webm-muxer (npm)
 */

import {
  Muxer as Mp4Muxer,
  ArrayBufferTarget as Mp4Target,
} from "mp4-muxer";
import {
  Muxer as WebmMuxer,
  ArrayBufferTarget as WebmTarget,
} from "webm-muxer";

export type VideoExt = "webm" | "mp4";

/**
 * SVG をレンダリングする viewBox 矩形 (SVG ユーザー単位)。
 * 元の SVG より大きい矩形を指定すると、wordmark の周囲に余白が生まれ、
 * その中に「o」の拡大もはみ出して描かれる (overflow="visible" を併用)。
 */
export interface ExportViewBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

// FullHD 60fps を狙うときに使う候補コーデック (前から順に supported チェック)。
// H.264 は profile/level を hex 6 桁で指定する: profile + constraints + level。
// 1920x1080@60 は H.264 level 4.2 (0x2A) 以上が必要。
const H264_CODECS = [
  "avc1.640032", // High profile, level 5.0 (余裕あり)
  "avc1.64002A", // High profile, level 4.2
  "avc1.4D002A", // Main profile, level 4.2
  "avc1.42002A", // Baseline profile, level 4.2
];
// VP9: profile 0 (8-bit 4:2:0), level 4.0 (1080p60 対応)
const VP9_CODECS = ["vp09.00.40.08", "vp09.00.10.08"];

function codecCandidates(ext: VideoExt): string[] {
  return ext === "mp4" ? H264_CODECS : VP9_CODECS;
}

/** Blob をローカルに `<name>.<ext>` としてダウンロードさせる。 */
export function downloadBlob(blob: Blob, name: string, ext: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${name}.${ext}`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * 指定拡張子で WebCodecs + muxer 経由の書き出しが可能かを判定する。
 * VideoEncoder が定義されていて、候補コーデックのどれかが supported なら true。
 */
export async function detectVideoFormat(
  ext: VideoExt,
  width = 1920,
  height = 1080,
  fps = 60,
  bitrate = 12_000_000,
): Promise<boolean> {
  if (typeof VideoEncoder === "undefined") return false;
  for (const codec of codecCandidates(ext)) {
    try {
      const r = await VideoEncoder.isConfigSupported({
        codec,
        width,
        height,
        framerate: fps,
        bitrate,
      });
      if (r.supported) return true;
    } catch {
      // 候補スキップ
    }
  }
  return false;
}

async function pickCodec(
  ext: VideoExt,
  width: number,
  height: number,
  fps: number,
  bitrate: number,
): Promise<string> {
  for (const codec of codecCandidates(ext)) {
    try {
      const r = await VideoEncoder.isConfigSupported({
        codec,
        width,
        height,
        framerate: fps,
        bitrate,
      });
      if (r.supported) return codec;
    } catch {
      /* skip */
    }
  }
  throw new Error(`No supported codec for ${ext}`);
}

/**
 * SVG → offscreen canvas のフレーム描画器。
 * draw() を呼ぶたびに SVG の現在状態が canvas に焼かれる。
 */
export class SvgFrameRenderer {
  readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly svg: SVGSVGElement;
  private readonly viewBox: ExportViewBox | null;

  constructor(
    svg: SVGSVGElement,
    width: number,
    height: number,
    viewBox: ExportViewBox | null = null,
  ) {
    this.svg = svg;
    this.viewBox = viewBox;
    this.canvas = document.createElement("canvas");
    this.canvas.width = width;
    this.canvas.height = height;
    const ctx = this.canvas.getContext("2d", { alpha: false });
    if (!ctx) throw new Error("Cannot get 2d canvas context");
    this.ctx = ctx;
  }

  /**
   * SVG を現在状態でクローンし、viewBox を上書き (任意) + overflow="visible" を
   * 設定 → Image でデコード → canvas に焼き付ける。bgColor は canvas 全面 + SVG
   * 内部背景 rect 両方に塗って透過を排除。
   */
  async draw(bgColor: string): Promise<void> {
    const clone = this.svg.cloneNode(true) as SVGSVGElement;
    if (this.viewBox) {
      clone.setAttribute(
        "viewBox",
        `${this.viewBox.x} ${this.viewBox.y} ${this.viewBox.w} ${this.viewBox.h}`,
      );
    }
    clone.setAttribute("overflow", "visible");
    clone.setAttribute("width", String(this.canvas.width));
    clone.setAttribute("height", String(this.canvas.height));

    const ns = "http://www.w3.org/2000/svg";
    const vb = clone.getAttribute("viewBox") || "0 0 100 100";
    const [vx, vy, vw, vh] = vb.split(/\s+/).map(Number);
    const bgRect = document.createElementNS(ns, "rect");
    bgRect.setAttribute("x", String(vx));
    bgRect.setAttribute("y", String(vy));
    bgRect.setAttribute("width", String(vw));
    bgRect.setAttribute("height", String(vh));
    bgRect.setAttribute("fill", bgColor);
    clone.insertBefore(bgRect, clone.firstChild);

    const xml = new XMLSerializer().serializeToString(clone);
    const blob = new Blob([xml], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    try {
      const img = new Image();
      await new Promise<void>((resolve, reject) => {
        img.onload = (): void => resolve();
        img.onerror = (): void => reject(new Error("svg decode failed"));
        img.src = url;
      });
      this.ctx.fillStyle = bgColor;
      this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
      this.ctx.drawImage(img, 0, 0, this.canvas.width, this.canvas.height);
    } finally {
      URL.revokeObjectURL(url);
    }
  }
}

/**
 * WebCodecs オフラインエンコーダ + muxer。フレーム毎に encodeFrame を呼び、
 * 最後に finalize で Blob を取り出す。再利用不可 (1 録画に 1 インスタンス)。
 */
export class OfflineVideoEncoder {
  private readonly encoder: VideoEncoder;
  // mp4-muxer / webm-muxer はジェネリック型が異なるので any にしておく。
  // (実体は addVideoChunk / finalize / target.buffer の同一インターフェース)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private readonly muxer: any;
  private readonly mimeType: string;
  private readonly fps: number;
  private encodedCount = 0;
  private errorState: Error | null = null;

  // muxer 側に届いたチャンクの数 (= encoder の output コールバックが
  // 呼ばれた回数)。0 のまま finalize に到達すると muxer が moov 生成で
  // null colorSpace エラーを投げるので、それより前にこちらで明確な
  // エラーを返したい。
  private chunkCount = 0;

  private constructor(
    encoder: VideoEncoder,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    muxer: any,
    mimeType: string,
    fps: number,
  ) {
    this.encoder = encoder;
    this.muxer = muxer;
    this.mimeType = mimeType;
    this.fps = fps;
  }

  static async create(
    ext: VideoExt,
    width: number,
    height: number,
    fps: number,
    bitrate: number,
  ): Promise<OfflineVideoEncoder> {
    if (typeof VideoEncoder === "undefined") {
      throw new Error("WebCodecs (VideoEncoder) is not supported");
    }
    const codec = await pickCodec(ext, width, height, fps, bitrate);

    let muxer:
      | Mp4Muxer<Mp4Target>
      | WebmMuxer<WebmTarget>;
    let mimeType: string;
    if (ext === "mp4") {
      muxer = new Mp4Muxer({
        target: new Mp4Target(),
        video: { codec: "avc", width, height, frameRate: fps },
        fastStart: "in-memory",
        // 最初のチャンクの timestamp を 0 へ自動オフセット (cube の実時間
        // 録画では performance.now() ベース + encoder 内部の reorder で
        // 最初のチャンクが非ゼロになることがあるため)。
        firstTimestampBehavior: "offset",
      });
      mimeType = "video/mp4";
    } else {
      muxer = new WebmMuxer({
        target: new WebmTarget(),
        video: { codec: "V_VP9", width, height, frameRate: fps },
        firstTimestampBehavior: "offset",
      });
      mimeType = "video/webm";
    }

    // 一旦コンテナとなる instance を作ってからクロージャで参照する。
    // eslint-disable-next-line prefer-const
    let self: OfflineVideoEncoder;
    let firstChunkSeen = false;
    const encoder = new VideoEncoder({
      output: (chunk, meta): void => {
        // 最初のチャンクには必ず decoderConfig が含まれるはず。
        // 含まれなければ muxer の moov 生成で colorSpace null エラーになるので
        // 早期に警告する (主にデバッグ用)。
        if (!firstChunkSeen) {
          firstChunkSeen = true;
          if (!meta?.decoderConfig) {
            console.warn(
              "[OfflineVideoEncoder] first chunk has no decoderConfig — muxer finalize will fail",
              { ext, codec, meta },
            );
          }
        }
        if (self) self.chunkCount++;
        muxer.addVideoChunk(chunk, meta);
      },
      error: (e): void => {
        // self は configure 完了 (= 同期実行) 後にだけ設定されるが、
        // 念のため undefined ガードを入れて TypeError を防ぐ。
        const err = e instanceof Error ? e : new Error(String(e));
        console.error("[OfflineVideoEncoder] encoder error:", err);
        if (self) self.errorState = err;
      },
    });
    encoder.configure({
      codec,
      width,
      height,
      framerate: fps,
      bitrate,
      // 実時間録画用 (cube) でも遅延を抑えてフレームを早く出力させる。
      // typography のオフラインでも問題ないので両方に適用。
      latencyMode: "realtime",
    });
    self = new OfflineVideoEncoder(encoder, muxer, mimeType, fps);
    return self;
  }

  /** エンコーダ内部のキューサイズ (バックプレッシャー判定用)。 */
  get queueSize(): number {
    return this.encoder.encodeQueueSize;
  }

  /**
   * canvas の現在内容を 1 フレームとしてエンコードキューに投入する。
   *
   * timestampUs を渡さなければ「frameCount / fps」で計算する (= オフライン
   * の決定論的タイムスタンプ)。渡された場合はそのマイクロ秒値をそのまま
   * 採用する (= 実時間録画で performance.now() を渡すユースケース)。
   *
   * エンコーダのキューが詰まったら自動で待機する (バックプレッシャー)。
   */
  async encodeFrame(
    canvas: HTMLCanvasElement,
    keyFrame: boolean,
    timestampUs?: number,
  ): Promise<void> {
    if (this.errorState) throw this.errorState;
    const ts =
      timestampUs ?? Math.round((this.encodedCount * 1_000_000) / this.fps);
    const frame = new VideoFrame(canvas, { timestamp: ts });
    try {
      this.encoder.encode(frame, { keyFrame });
    } finally {
      frame.close();
    }
    this.encodedCount++;

    // バックプレッシャー: キューが溜まりすぎたらエンコーダの追いつきを待つ。
    while (this.encoder.encodeQueueSize > 30) {
      await new Promise<void>((r) => setTimeout(r, 5));
      if (this.errorState) throw this.errorState;
    }
  }

  /** 残りキューを flush して muxer を閉じ、Blob を返す。 */
  async finalize(): Promise<Blob> {
    await this.encoder.flush();
    if (this.errorState) throw this.errorState;
    this.encoder.close();
    // flush 後でも muxer に 1 件もチャンクが届いていない場合、muxer.finalize
    // は decoderConfig が null のままで colorSpace null エラーを投げる。
    // ここで先に分かりやすいエラーに置き換える。
    if (this.chunkCount === 0) {
      throw new Error(
        "encoder produced no chunks (encode may have been called too few times before finalize)",
      );
    }
    this.muxer.finalize();
    const buffer: ArrayBuffer = this.muxer.target.buffer;
    return new Blob([buffer], { type: this.mimeType });
  }
}

/**
 * 「WebGL canvas + DOM オーバーレイ要素」を 1 枚の export canvas に焼き直す
 * コンポジター。CubeLogo の動画書き出し用 (WebGL のキューブ + CSS2D の
 * ドット/ラベルを同時にキャプチャ)。
 *
 * 描画は毎フレーム同期に行う。getBoundingClientRect / getComputedStyle を
 * 各要素で都度引くため要素数が増えると重くなるが、典型的な ~50 要素なら
 * 1 フレーム内で十分間に合う。
 */
export class CanvasFrameCompositor {
  readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;

  constructor(width: number, height: number) {
    this.canvas = document.createElement("canvas");
    this.canvas.width = width;
    this.canvas.height = height;
    const ctx = this.canvas.getContext("2d", { alpha: false });
    if (!ctx) throw new Error("Cannot get 2d canvas context");
    this.ctx = ctx;
  }

  /**
   * src (WebGL canvas) を contain-fit でコピーし、overlayRoot 配下の
   * `.vertex-dot` (円) / `.vertex-label` / `.axis-label` (テキスト + 背景)
   * を同じ scale/offset で重ね描きする。
   *
   * src のサイズはブラウザのレイアウト座標 (= overlayRoot と同じ座標系) と
   * 一致している前提。typeographyLogo のように特殊な viewBox を持たないため、
   * 単純な linear scale + translate でマップできる。
   */
  composite(opts: {
    src: HTMLCanvasElement;
    srcWidth: number;
    srcHeight: number;
    overlayRoot: HTMLElement;
    bgColor: string;
  }): void {
    const { src, srcWidth, srcHeight, overlayRoot, bgColor } = opts;
    const canvas = this.canvas;
    const ctx = this.ctx;

    // contain-fit: src のアスペクトを保ったまま canvas 内に最大化、余白を bg で埋める。
    const srcAspect = srcWidth / srcHeight;
    const dstAspect = canvas.width / canvas.height;
    let drawW: number;
    let drawH: number;
    let drawX: number;
    let drawY: number;
    if (srcAspect >= dstAspect) {
      drawW = canvas.width;
      drawH = canvas.width / srcAspect;
      drawX = 0;
      drawY = (canvas.height - drawH) / 2;
    } else {
      drawH = canvas.height;
      drawW = canvas.height * srcAspect;
      drawX = (canvas.width - drawW) / 2;
      drawY = 0;
    }

    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(src, drawX, drawY, drawW, drawH);

    // DOM オーバーレイの座標 → export canvas 座標
    const sx = drawW / srcWidth;
    const sy = drawH / srcHeight;
    const overlayRect = overlayRoot.getBoundingClientRect();
    const elements = overlayRoot.querySelectorAll<HTMLElement>(
      ".vertex-dot, .vertex-label, .axis-label",
    );

    for (const el of elements) {
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) continue;
      const style = window.getComputedStyle(el);
      if (style.display === "none" || style.visibility === "hidden") continue;
      const opacity = parseFloat(style.opacity);
      if (opacity < 0.01) continue;

      // overlayRoot ローカル座標
      const ex = rect.left - overlayRect.left;
      const ey = rect.top - overlayRect.top;
      const ew = rect.width;
      const eh = rect.height;

      ctx.globalAlpha = opacity;

      if (el.classList.contains("vertex-dot")) {
        // 円: 中心 + 半径 (大きい方の sx/sy を採用してアスペクト崩れに耐える)
        const cx = ex + ew / 2;
        const cy = ey + eh / 2;
        const r = (Math.min(ew, eh) / 2) * Math.max(sx, sy);
        ctx.fillStyle = style.backgroundColor;
        ctx.beginPath();
        ctx.arc(drawX + cx * sx, drawY + cy * sy, r, 0, Math.PI * 2);
        ctx.fill();
      } else {
        // ラベル: 背景 rect (透明でなければ) + テキスト
        const bg = style.backgroundColor;
        const hasBg =
          bg && bg !== "transparent" && !/rgba?\(.*?, ?0\)$/.test(bg);
        if (hasBg) {
          ctx.fillStyle = bg;
          ctx.fillRect(
            drawX + ex * sx,
            drawY + ey * sy,
            ew * sx,
            eh * sy,
          );
        }
        const fontSize = parseFloat(style.fontSize) * sy;
        const fontFamily = style.fontFamily.replace(/["']/g, "");
        const fontWeight = style.fontWeight;
        ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
        ctx.fillStyle = style.color;
        ctx.textBaseline = "middle";
        const padLeft = parseFloat(style.paddingLeft) || 0;
        ctx.fillText(
          el.textContent || "",
          drawX + (ex + padLeft) * sx,
          drawY + (ey + eh / 2) * sy,
        );
      }

      ctx.globalAlpha = 1;
    }
  }
}

